import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, query, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { GraduationCap, Trash2, CheckCircle, Search, Filter, ChevronUp, ChevronDown, FileSpreadsheet, Pin, Plus, X, BookOpen, Building, Award, CreditCard, User, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PartnerContext } from '../App';

const StudentManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCountry, setFilterCountry] = useState('all');
    const [filterRepCode, setFilterRepCode] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedSheetMsg, setCopiedSheetMsg] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Freeze Panes / Pinning State ('name' | 'email' | 'none')
    const [pinnedColumn, setPinnedColumn] = useState('name');

    // Comprehensive Form State (Matching Complete Onboarding Pathways)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [nationalId, setNationalId] = useState('');
    const [country, setCountry] = useState('Kenya');
    const [county, setCounty] = useState('Nairobi');
    const [institution, setInstitution] = useState('The East African University');
    const [candidateProgramme, setCandidateProgramme] = useState('Software Engineering & AI');
    const [campusId, setCampusId] = useState('online');
    const [cohort, setCohort] = useState('Class of 2026');
    const [eduLevel, setEduLevel] = useState("Bachelor's Degree");
    const [previousInstitution, setPreviousInstitution] = useState('');
    const [graduationYear, setGraduationYear] = useState('');
    const [sponsorship, setSponsorship] = useState('self');
    const [sponsor, setSponsor] = useState('');
    const [salesRepCode, setSalesRepCode] = useState('');
    const [studentStatus, setStudentStatus] = useState('Paid 1020');
    const [transactionCode, setTransactionCode] = useState('');
    const [profileImage, setProfileImage] = useState('');
    const [bio, setBio] = useState('');

    const deduplicateStudents = (studentList) => {
        const studentMap = new Map();

        studentList.forEach(student => {
            const emailKey = (student.email || (student.id?.includes('@') ? student.id : '')).trim().toLowerCase();
            const appRefKey = student.applicationRef || (student.id?.startsWith('DIS-') ? student.id : null);
            
            // Match existing student by canonical email or applicationRef
            let existingKey = null;
            if (emailKey && studentMap.has(emailKey)) {
                existingKey = emailKey;
            } else if (appRefKey) {
                for (const [k, existing] of studentMap.entries()) {
                    const existingAppRef = existing.applicationRef || (existing.id?.startsWith('DIS-') ? existing.id : null);
                    const existingEmail = (existing.email || (existing.id?.includes('@') ? existing.id : '')).trim().toLowerCase();
                    if (existingAppRef === appRefKey || (emailKey && existingEmail === emailKey)) {
                        existingKey = k;
                        break;
                    }
                }
            }

            if (!existingKey) {
                const primaryKey = emailKey || student.id;
                studentMap.set(primaryKey, { ...student });
            } else {
                const existing = studentMap.get(existingKey);
                
                // Prioritize active/paid status tags
                const isHighPriority = (st) => ['Paid 1020', 'Paid 1000', 'PAID', 'Active', 'Current'].includes(st);
                const resolvedStatus = isHighPriority(student.status) ? student.status : (isHighPriority(existing.status) ? existing.status : (student.status || existing.status));
                
                // Prioritize canonical ID for navigation (email or Google UID over temporary DIS-xxxx document ID)
                const preferredId = (existing.id?.includes('@') || !existing.id?.startsWith('DIS-')) ? existing.id : student.id;

                const merged = {
                    ...existing,
                    ...student,
                    id: preferredId,
                    status: resolvedStatus,
                    fee_status: (student.fee_status === 'PAID' || existing.fee_status === 'PAID') ? 'PAID' : (student.fee_status || existing.fee_status),
                    applicationRef: student.applicationRef || existing.applicationRef || appRefKey,
                    name: (student.name && student.name !== 'Student') ? student.name : (existing.name || student.name),
                    email: student.email || existing.email || emailKey,
                    phone: student.phone || existing.phone || '',
                    country: student.country || existing.country || '',
                    county: student.county || existing.county || '',
                    cohort: (student.cohort && student.cohort !== 'Unknown') ? student.cohort : (existing.cohort || 'Unknown'),
                    salesRepCode: student.salesRepCode || existing.salesRepCode || '',
                    sponsorship: (student.sponsorship && student.sponsorship !== 'N/A') ? student.sponsorship : (existing.sponsorship || 'N/A'),
                    sponsor: student.sponsor || existing.sponsor || '',
                    institution: student.institution || existing.institution || '',
                    candidateProgramme: student.candidateProgramme || existing.candidateProgramme || student.programName || existing.programName || '',
                    cohortId: student.cohortId || existing.cohortId || '',
                    ecosystemId: student.ecosystemId || existing.ecosystemId || student.uid || existing.uid || emailKey
                };
                studentMap.set(existingKey, merged);
            }
        });

        return Array.from(studentMap.values());
    };

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'students'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));
            
            // Apply Ecosystem ID-anchored deduplication
            let unifiedData = deduplicateStudents(data);

            if (showDemo) {
                unifiedData.unshift(...getDemoDataForCollection('students'));
            }
            setStudents(unifiedData);
        } catch (e) {
            console.error("Error fetching students:", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchStudents();
    }, [showDemo]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!email.trim() || !email.includes('@')) return;
        setStatusMessage('saving');
        
        const cleanEmail = email.trim().toLowerCase();
        
        // Auto-generate a simple, non-hackable Onboarding ID (e.g. DIS-26-X8F9A2)
        const generateOnboardingID = () => {
            const year = new Date().getFullYear().toString().slice(-2);
            const randomHash = Math.random().toString(36).substring(2, 8).toUpperCase();
            return `DIS-${year}-${randomHash}`;
        };
        
        try {
            // STRICTLY NO SYSTEM_ROLES WRITE. Only ecosystem database profile.
            await setDoc(doc(db, 'students', cleanEmail), {
                name: name.trim(),
                displayName: name.trim(),
                email: cleanEmail,
                phone: phone.trim(),
                nationalId: nationalId.trim(),
                country: country.trim() || 'Kenya',
                county: county.trim() || 'Nairobi',
                institution: institution.trim(),
                partnerId: institution.toLowerCase().includes('east african') ? 'teau' : 'dis',
                referringOrg: institution.toLowerCase().includes('east african') ? 'TEAU' : 'DIS',
                candidateProgramme: candidateProgramme.trim(),
                programName: candidateProgramme.trim(),
                campusId: campusId,
                cohort: cohort,
                eduLevel: eduLevel,
                previousInstitution: previousInstitution.trim(),
                graduationYear: graduationYear.trim(),
                sponsorship: sponsorship,
                sponsor: sponsor.trim(),
                salesRepCode: salesRepCode.trim().toUpperCase(),
                status: studentStatus,
                fee_status: (studentStatus === 'Paid 1020' || studentStatus === 'Paid 1000' || studentStatus === 'Current' || studentStatus === 'Active') ? 'PAID' : 'PENDING',
                payment_status: (studentStatus === 'Paid 1020' || studentStatus === 'Paid 1000') ? 'paid_onboarding' : studentStatus,
                transactionCode: transactionCode.trim(),
                mpesaReceiptNumber: transactionCode.trim(),
                profileImage: profileImage.trim(),
                bio: bio.trim(),
                applicationRef: generateOnboardingID(),
                addedAt: new Date().toISOString()
            });

            setName('');
            setEmail('');
            setPhone('');
            setNationalId('');
            setPreviousInstitution('');
            setGraduationYear('');
            setSponsor('');
            setSalesRepCode('');
            setTransactionCode('');
            setProfileImage('');
            setBio('');
            setIsFormOpen(false);
            setStatusMessage('success');
            setTimeout(() => setStatusMessage(''), 3000);
            fetchStudents();
        } catch (error) {
            console.error(error);
            setStatusMessage('error');
        }
    };

    const handleDelete = async (studentId) => {
        if (!window.confirm(`Permanently remove ${studentId} from the student registry?`)) return;
        try {
            await deleteDoc(doc(db, 'students', studentId));
            fetchStudents();
        } catch (error) {
            console.error(error);
        }
    };

    const { activePartner } = React.useContext(PartnerContext);

    const filteredStudents = students.filter(s => {
        const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
        const matchesCountry = filterCountry === 'all' || s.country === filterCountry;
        const matchesRepCode = filterRepCode === 'all' || (s.salesRepCode || '').toUpperCase() === filterRepCode.toUpperCase();
        
        let matchesPartner = true;
        if (activePartner !== 'global') {
            if (activePartner === 'teau') {
                matchesPartner = s.partnerId === 'teau' || 
                                 (s.institution && s.institution.toLowerCase().includes('east african')) || 
                                 (s.referringOrg && s.referringOrg.toUpperCase() === 'TEAU');
            } else {
                matchesPartner = s.partnerId === activePartner;
            }
        }

        let matchesSearch = true;
        if (searchTerm.trim()) {
            const q = searchTerm.trim().toLowerCase();
            const n = (s.name || s.displayName || `${s.firstName || ''} ${s.lastName || ''}`).toLowerCase();
            const e = (s.email || '').toLowerCase();
            const c = (s.cohort || '').toLowerCase();
            const l = ((s.country || '') + ' ' + (s.county || '')).toLowerCase();
            const r = (s.salesRepCode || '').toLowerCase();
            const sp = (s.sponsor || '').toLowerCase();
            const st = (s.status || '').toLowerCase();
            matchesSearch = n.includes(q) || e.includes(q) || c.includes(q) || l.includes(q) || r.includes(q) || sp.includes(q) || st.includes(q);
        }

        return matchesStatus && matchesCountry && matchesRepCode && matchesPartner && matchesSearch;
    });

    // Extract unique countries & sales rep codes for the filter dropdowns
    const uniqueCountries = [...new Set(students.map(s => s.country).filter(Boolean))].sort();
    const uniqueRepCodes = [...new Set(students.map(s => s.salesRepCode).filter(Boolean))].sort();

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedStudents = [...filteredStudents].sort((a, b) => {
        if (!sortConfig.key) return 0;
        
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';
        
        if (sortConfig.key === 'name') {
            aValue = a.name || a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Unnamed';
            bValue = b.name || b.displayName || `${b.firstName || ''} ${b.lastName || ''}`.trim() || 'Unnamed';
        }
        
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const paidCount = students.filter(s => ['Paid 1020', 'Paid 1000', 'PAID', 'Active', 'Current'].includes(s.status)).length;
    const sponsoredCount = students.filter(s => Boolean(s.sponsor) || s.sponsorship === 'corporate' || s.sponsorship === 'nef').length;

    const copyToGoogleSheets = () => {
        const headers = ['Row #', 'Student Name', 'Email', 'Agent Code', 'Cohort', 'Country', 'County', 'Status', 'Funding', 'Sponsor', 'Ecosystem ID'];
        const rows = sortedStudents.map((s, idx) => [
            idx + 1,
            (s.name || s.displayName || `${s.firstName || ''} ${s.lastName || ''}`).trim() || 'Unnamed',
            s.email || '',
            s.salesRepCode || 'Direct / None',
            s.cohort || 'Unknown',
            s.country || '',
            s.county || '',
            s.status || 'Pending',
            s.sponsorship || 'N/A',
            s.sponsor || '',
            s.id || ''
        ]);
        const tsv = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
        navigator.clipboard.writeText(tsv);
        setCopiedSheetMsg(true);
        setTimeout(() => setCopiedSheetMsg(false), 2500);
    };

    return (
        <div className="space-y-8">
            <header>
                <h1 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Student Registry</h1>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mt-2">Manage Student Ecosystem Profiles</p>
            </header>

            {/* Comprehensive Add Student Form Accordion */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden shadow-sm">
                <div 
                    onClick={() => setIsFormOpen(!isFormOpen)}
                    className="p-5 flex items-center justify-between cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors select-none"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#8B1A2D]/10 dark:bg-[#8B1A2D]/20 border border-[#8B1A2D]/30 dark:border-[#8B1A2D]/40 flex items-center justify-center text-[#8B1A2D]">
                            <Plus size={16} />
                        </div>
                        <div>
                            <h2 className="text-xs font-black uppercase tracking-widest text-zinc-900 dark:text-white flex items-center gap-2">
                                Register Candidate (Full Onboarding Dataset)
                            </h2>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 font-medium">Complete admission file: Identity, Academic Track, Campus, Education &amp; Funding</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                            {isFormOpen ? 'Collapse Form' : 'Expand Form'}
                        </span>
                        {isFormOpen ? <ChevronUp size={16} className="text-zinc-500 dark:text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-500 dark:text-zinc-400" />}
                    </div>
                </div>

                {isFormOpen && (
                    <form onSubmit={handleAdd} className="p-6 pt-2 border-t border-zinc-100 dark:border-zinc-800 space-y-6">
                        {/* Section 1: Candidate Identity & Contact */}
                        <div>
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#8B1A2D] mb-3 flex items-center gap-1.5">
                                <User size={13} /> 1. Personal Identity &amp; Contact Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Full Legal Name *</label>
                                    <input 
                                        type="text" 
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder="e.g. John Kamau Mwangi"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Email Address *</label>
                                    <input 
                                        type="email" 
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="candidate@gmail.com"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-mono placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Phone / WhatsApp Number</label>
                                    <input 
                                        type="text" 
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        placeholder="+254 700 000 000"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">National ID / Passport No.</label>
                                    <input 
                                        type="text" 
                                        value={nationalId}
                                        onChange={e => setNationalId(e.target.value)}
                                        placeholder="e.g. 34567890"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Country</label>
                                    <input 
                                        type="text" 
                                        value={country}
                                        onChange={e => setCountry(e.target.value)}
                                        placeholder="Kenya"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">County / City</label>
                                    <input 
                                        type="text" 
                                        value={county}
                                        onChange={e => setCounty(e.target.value)}
                                        placeholder="Nairobi / Machakos / Kiambu"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Programme, Institutional Pathway & Campus */}
                        <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800/80">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-1.5">
                                <Building size={13} /> 2. Admission Pathway &amp; Programme Selection
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Partner Institution Track</label>
                                    <select
                                        value={institution}
                                        onChange={e => setInstitution(e.target.value)}
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-bold focus:outline-none focus:border-[#8B1A2D]"
                                    >
                                        <option value="The East African University">The East African University (TEAU)</option>
                                        <option value="Direct DIS Ecosystem">Direct DIS Ecosystem</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Enrolled Programme / Course</label>
                                    <input 
                                        type="text" 
                                        value={candidateProgramme}
                                        onChange={e => setCandidateProgramme(e.target.value)}
                                        placeholder="e.g. Software Engineering & AI"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Campus Allocation</label>
                                    <select
                                        value={campusId}
                                        onChange={e => setCampusId(e.target.value)}
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-bold focus:outline-none focus:border-[#8B1A2D]"
                                    >
                                        <option value="online">Online / Virtual Learning</option>
                                        <option value="main">Main Campus - Kitengela</option>
                                        <option value="nairobi_city">Nairobi City Campus</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Cohort Intake</label>
                                    <select
                                        value={cohort}
                                        onChange={e => setCohort(e.target.value)}
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-bold focus:outline-none focus:border-[#8B1A2D]"
                                    >
                                        <option value="Class of 2026">Class of 2026</option>
                                        <option value="Class of 2027">Class of 2027</option>
                                        <option value="Class of 2028">Class of 2028</option>
                                        <option value="Class of 2025">Class of 2025</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Academic Background */}
                        <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800/80">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400 mb-3 flex items-center gap-1.5">
                                <Award size={13} /> 3. Prior Educational Background
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Highest Qualification</label>
                                    <select
                                        value={eduLevel}
                                        onChange={e => setEduLevel(e.target.value)}
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-bold focus:outline-none focus:border-[#8B1A2D]"
                                    >
                                        <option value="Bachelor's Degree">Bachelor's Degree</option>
                                        <option value="Diploma">Diploma</option>
                                        <option value="Certificate">Certificate</option>
                                        <option value="High School (KCSE)">High School (KCSE)</option>
                                        <option value="Master's Degree">Master's Degree</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Prior Alma Mater / Institution</label>
                                    <input 
                                        type="text" 
                                        value={previousInstitution}
                                        onChange={e => setPreviousInstitution(e.target.value)}
                                        placeholder="e.g. Chuka University / Kenyatta Univ"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Graduation Year</label>
                                    <input 
                                        type="text" 
                                        value={graduationYear}
                                        onChange={e => setGraduationYear(e.target.value)}
                                        placeholder="e.g. 2024"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 4: Funding, Sponsorship & Gate 1 Screening */}
                        <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800/80">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-1.5">
                                <CreditCard size={13} /> 4. Funding, Corporate Sponsorship &amp; Status
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Funding Mechanism</label>
                                    <select
                                        value={sponsorship}
                                        onChange={e => setSponsorship(e.target.value)}
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-bold focus:outline-none focus:border-[#8B1A2D]"
                                    >
                                        <option value="self">Self-Funded (Standard)</option>
                                        <option value="pay_full">Self-Funded (Pay in Full)</option>
                                        <option value="pay_slowly">Self-Funded (Installments)</option>
                                        <option value="corporate">Corporate / Institutional Sponsor</option>
                                        <option value="nef">Bursary / NEF Sponsored</option>
                                        <option value="unsure">Needs Guidance / Applying for Sponsor</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Sponsoring Entity (if applicable)</label>
                                    <input 
                                        type="text" 
                                        value={sponsor}
                                        onChange={e => setSponsor(e.target.value)}
                                        placeholder="e.g. The East African University / Kenya Airways"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Sales Rep / Agent Referral Code</label>
                                    <input 
                                        type="text" 
                                        value={salesRepCode}
                                        onChange={e => setSalesRepCode(e.target.value)}
                                        placeholder="e.g. REP-DIS-01"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-amber-700 dark:text-amber-300 font-mono placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Gate 1 Admission Status</label>
                                    <select
                                        value={studentStatus}
                                        onChange={e => setStudentStatus(e.target.value)}
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-bold focus:outline-none focus:border-[#8B1A2D]"
                                    >
                                        <option value="Paid 1020">Paid 1020 (Gate 1 Cleared)</option>
                                        <option value="Paid 1000">Paid 1000 (Legacy Paid)</option>
                                        <option value="Current">Current / Active</option>
                                        <option value="pending_verification">Pending Verification</option>
                                        <option value="pending_payment">Pending Payment</option>
                                        <option value="Pending">Pending Application</option>
                                        <option value="Alumni">Alumni</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">M-Pesa Transaction Ref</label>
                                    <input 
                                        type="text" 
                                        value={transactionCode}
                                        onChange={e => setTransactionCode(e.target.value)}
                                        placeholder="e.g. QWE8910XYZ"
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white font-mono placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-1">Profile Image URL</label>
                                    <input 
                                        type="url" 
                                        value={profileImage}
                                        onChange={e => setProfileImage(e.target.value)}
                                        placeholder="https://..."
                                        className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-xs text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-[#8B1A2D]"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                            <button 
                                type="button"
                                onClick={() => setIsFormOpen(false)}
                                className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-white dark:hover:bg-zinc-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={statusMessage === 'saving'}
                                className="bg-[#8B1A2D] hover:bg-[#6c1423] text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-md"
                            >
                                {statusMessage === 'saving' ? 'Registering...' : 'Register Candidate Profile'}
                            </button>
                        </div>
                    </form>
                )}
                {statusMessage === 'success' && <p className="text-emerald-600 dark:text-emerald-400 text-xs font-bold p-4 pt-0 flex items-center gap-1.5 justify-end"><CheckCircle size={14} /> Full student profile registered successfully.</p>}
            </div>

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden w-full shadow-sm">
                {/* Spreadsheet Toolbar */}
                <div className="p-3.5 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/80 dark:bg-zinc-900/90 flex flex-wrap gap-3 justify-between items-center">
                    <div className="flex flex-wrap gap-2.5 items-center flex-1">
                        {/* Search input */}
                        <div className="relative min-w-[220px] max-w-xs">
                            <Search size={13} className="absolute left-3 top-2.5 text-zinc-400 dark:text-zinc-500" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search spreadsheet..."
                                className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-zinc-900 dark:text-white font-medium placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-500"
                            />
                        </div>

                        {/* Freeze Pane Selector */}
                        <div className="flex items-center gap-1.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-zinc-700 dark:text-zinc-300 shadow-2xs">
                            <Pin size={12} className={pinnedColumn !== 'none' ? 'text-amber-500 fill-amber-500' : 'text-zinc-400 dark:text-zinc-500'} />
                            <span className="text-zinc-500 dark:text-zinc-400 uppercase text-[10px] tracking-wider">Freeze:</span>
                            <select 
                                value={pinnedColumn} 
                                onChange={e => setPinnedColumn(e.target.value)} 
                                className="bg-transparent text-amber-600 dark:text-amber-400 font-black uppercase tracking-wider focus:outline-none cursor-pointer text-[11px]"
                            >
                                <option value="name" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">📌 Pin Name</option>
                                <option value="email" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">📌 Pin Email</option>
                                <option value="none" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">🔓 Unfreeze</option>
                            </select>
                        </div>

                        <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700 mx-1 hidden sm:block"></div>

                        {/* Status Filter */}
                        <div className="flex items-center gap-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-2.5 py-1.5 shadow-2xs">
                            <Filter size={11} className="text-zinc-400 dark:text-zinc-500" />
                            <select
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value)}
                                className="bg-transparent text-zinc-800 dark:text-zinc-200 font-bold text-[11px] uppercase tracking-wider focus:outline-none cursor-pointer"
                            >
                                <option value="all" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">All Statuses</option>
                                <option value="Current" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Current</option>
                                <option value="Paid 1020" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Paid 1020</option>
                                <option value="Paid 1000" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Paid 1000</option>
                                <option value="pending_verification" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Pending Verification</option>
                                <option value="pending_payment" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Pending Payment</option>
                                <option value="Pending" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Pending Application</option>
                                <option value="Alumni" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">Alumni</option>
                            </select>
                        </div>

                        {/* Country Filter */}
                        <div className="flex items-center gap-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-2.5 py-1.5 shadow-2xs">
                            <select
                                value={filterCountry}
                                onChange={e => setFilterCountry(e.target.value)}
                                className="bg-transparent text-zinc-800 dark:text-zinc-200 font-bold text-[11px] uppercase tracking-wider focus:outline-none cursor-pointer"
                            >
                                <option value="all" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">All Countries</option>
                                {uniqueCountries.map(c => (
                                    <option key={c} value={c} className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">{c}</option>
                                ))}
                            </select>
                        </div>

                        {/* Sales Rep Filter */}
                        <div className="flex items-center gap-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-2.5 py-1.5 shadow-2xs">
                            <select
                                value={filterRepCode}
                                onChange={e => setFilterRepCode(e.target.value)}
                                className="bg-transparent text-amber-600 dark:text-amber-400 font-bold text-[11px] uppercase tracking-wider focus:outline-none cursor-pointer"
                            >
                                <option value="all" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">All Sales Reps</option>
                                {uniqueRepCodes.map(r => (
                                    <option key={r} value={r} className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white">{r}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Copy for Google Sheets button */}
                        <button
                            onClick={copyToGoogleSheets}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/80 hover:bg-emerald-100 dark:hover:bg-emerald-900/90 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold uppercase tracking-wider transition-all shadow-2xs"
                            title="Copies entire filtered sheet to clipboard formatted for Google Sheets (paste with Ctrl+V)"
                        >
                            <FileSpreadsheet size={13} className="text-emerald-600 dark:text-emerald-400" />
                            <span>{copiedSheetMsg ? 'Copied TSV!' : 'Copy to Sheets'}</span>
                        </button>

                        <button 
                            onClick={() => setShowDemo(!showDemo)}
                            className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all shadow-2xs shrink-0 ${showDemo ? 'border-[#8B1A2D] text-white bg-[#8B1A2D] hover:bg-[#6c1423]' : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 bg-white dark:bg-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-500 hover:text-zinc-900 dark:hover:text-white'}`}
                        >
                            {showDemo ? 'Demo On' : 'Demo Off'}
                        </button>
                    </div>
                </div>

                {/* Horizontal Scroll Container for Sheet Grid with Freeze Panes */}
                <div className="overflow-x-auto custom-scrollbar w-full">
                    <table className="w-full text-left border-collapse min-w-[1100px]">
                        <thead>
                            <tr className="bg-zinc-100/90 dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800 select-none">
                                <th className={`w-12 p-3 text-center text-[10px] font-mono font-bold text-zinc-500 dark:text-zinc-400 border-r border-zinc-200 dark:border-zinc-800 ${
                                    pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-100 dark:bg-zinc-950' : ''
                                }`}>
                                    #
                                </th>
                                <th className={`min-w-[190px] p-0 border-r border-zinc-200 dark:border-zinc-800 ${
                                    pinnedColumn === 'name' 
                                        ? 'sticky left-12 z-30 bg-zinc-100 dark:bg-zinc-950 border-r-2 border-r-amber-500 shadow-[4px_0_12px_rgba(0,0,0,0.06)] dark:shadow-[4px_0_12px_rgba(0,0,0,0.8)]' 
                                        : pinnedColumn === 'email' 
                                        ? 'sticky left-12 z-30 bg-zinc-100 dark:bg-zinc-950' 
                                        : ''
                                }`}>
                                    <div className="w-full h-full p-3 flex items-center justify-between">
                                        <button onClick={() => handleSort('name')} className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                            <span>Name</span> {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                        </button>
                                        <button
                                            onClick={() => setPinnedColumn(pinnedColumn === 'name' ? 'none' : 'name')}
                                            className={`p-1 rounded transition-colors ${pinnedColumn === 'name' ? 'text-amber-500' : 'text-zinc-400 hover:text-zinc-700 dark:text-zinc-600 dark:hover:text-zinc-300'}`}
                                            title={pinnedColumn === 'name' ? 'Unpin Name column' : 'Pin Name column to left (freeze pane)'}
                                        >
                                            <Pin size={11} className={pinnedColumn === 'name' ? 'fill-amber-500' : ''} />
                                        </button>
                                    </div>
                                </th>
                                <th className={`min-w-[220px] p-0 border-r border-zinc-200 dark:border-zinc-800 ${
                                    pinnedColumn === 'email' 
                                        ? 'sticky left-[236px] z-30 bg-zinc-100 dark:bg-zinc-950 border-r-2 border-r-amber-500 shadow-[4px_0_12px_rgba(0,0,0,0.06)] dark:shadow-[4px_0_12px_rgba(0,0,0,0.8)]' 
                                        : ''
                                }`}>
                                    <div className="w-full h-full p-3 flex items-center justify-between">
                                        <button onClick={() => handleSort('email')} className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                            <span>Email Address</span> {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                        </button>
                                        <button
                                            onClick={() => setPinnedColumn(pinnedColumn === 'email' ? 'none' : 'email')}
                                            className={`p-1 rounded transition-colors ${pinnedColumn === 'email' ? 'text-amber-500' : 'text-zinc-400 hover:text-zinc-700 dark:text-zinc-600 dark:hover:text-zinc-300'}`}
                                            title={pinnedColumn === 'email' ? 'Unpin Email column' : 'Pin Email column to left (freeze pane)'}
                                        >
                                            <Pin size={11} className={pinnedColumn === 'email' ? 'fill-amber-500' : ''} />
                                        </button>
                                    </div>
                                </th>
                                <th className="min-w-[120px] p-0 border-r border-zinc-200 dark:border-zinc-800">
                                    <button onClick={() => handleSort('salesRepCode')} className="w-full h-full p-3 flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                        <span>Agent Code</span> {sortConfig.key === 'salesRepCode' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                    </button>
                                </th>
                                <th className="min-w-[130px] p-0 border-r border-zinc-200 dark:border-zinc-800">
                                    <button onClick={() => handleSort('cohort')} className="w-full h-full p-3 flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                        <span>Cohort</span> {sortConfig.key === 'cohort' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                    </button>
                                </th>
                                <th className="min-w-[150px] p-0 border-r border-zinc-200 dark:border-zinc-800">
                                    <button onClick={() => handleSort('country')} className="w-full h-full p-3 flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                        <span>Location</span> {sortConfig.key === 'country' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                    </button>
                                </th>
                                <th className="min-w-[140px] p-0 border-r border-zinc-200 dark:border-zinc-800">
                                    <button onClick={() => handleSort('status')} className="w-full h-full p-3 flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                        <span>Status</span> {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                    </button>
                                </th>
                                <th className="min-w-[160px] p-0 border-r border-zinc-200 dark:border-zinc-800">
                                    <button onClick={() => handleSort('sponsorship')} className="w-full h-full p-3 flex items-center justify-between text-[11px] font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white transition-colors">
                                        <span>Funding</span> {sortConfig.key === 'sponsorship' && (sortConfig.direction === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                                    </button>
                                </th>
                                <th className="w-16 p-3 text-[11px] font-black uppercase tracking-wider text-zinc-500 dark:text-zinc-400 text-center">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800 font-medium">
                            {loading ? (
                                <tr><td colSpan="9" className="p-8 text-center text-zinc-500 dark:text-zinc-400 text-xs">Loading Spreadsheet Registry...</td></tr>
                            ) : sortedStudents.length === 0 ? (
                                <tr><td colSpan="9" className="p-8 text-center text-zinc-500 dark:text-zinc-400 text-xs">No students match current filter criteria.</td></tr>
                            ) : sortedStudents.map((student, idx) => (
                                <tr 
                                    key={student.id} 
                                    onClick={() => navigate(`/students/${student.id}`)}
                                    className="hover:bg-zinc-50/80 dark:hover:bg-zinc-800/60 transition-colors cursor-pointer border-b border-zinc-200 dark:border-zinc-800 group"
                                >
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 dark:text-zinc-400 border-r border-zinc-200 dark:border-zinc-800 ${
                                        pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-white dark:bg-zinc-900 group-hover:bg-zinc-50 dark:group-hover:bg-zinc-800' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>
                                    <td className={`p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap ${
                                        pinnedColumn === 'name' 
                                            ? 'sticky left-12 z-20 bg-white dark:bg-zinc-900 group-hover:bg-zinc-50 dark:group-hover:bg-zinc-800 border-r-2 border-r-amber-500 shadow-[4px_0_12px_rgba(0,0,0,0.06)] dark:shadow-[4px_0_12px_rgba(0,0,0,0.8)]' 
                                            : pinnedColumn === 'email' 
                                            ? 'sticky left-12 z-20 bg-white dark:bg-zinc-900 group-hover:bg-zinc-50 dark:group-hover:bg-zinc-800' 
                                            : ''
                                    }`}>
                                        <p className="font-bold text-xs text-zinc-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                                            {student.name || student.displayName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unnamed'}
                                        </p>
                                    </td>
                                    <td className={`p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap ${
                                        pinnedColumn === 'email' 
                                            ? 'sticky left-[236px] z-20 bg-white dark:bg-zinc-900 group-hover:bg-zinc-50 dark:group-hover:bg-zinc-800 border-r-2 border-r-amber-500 shadow-[4px_0_12px_rgba(0,0,0,0.06)] dark:shadow-[4px_0_12px_rgba(0,0,0,0.8)]' 
                                            : ''
                                    }`}>
                                        <p className="font-mono text-xs text-zinc-600 dark:text-zinc-300">{student.email}</p>
                                    </td>
                                    <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap">
                                        <span className="font-mono text-[10px] font-bold text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/80 border border-amber-300 dark:border-amber-600/60 px-2 py-0.5 rounded shadow-2xs">
                                            {student.salesRepCode || 'Direct / None'}
                                        </span>
                                    </td>
                                    <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap">
                                        <span className="text-xs font-bold text-zinc-800 dark:text-white bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1 rounded border border-zinc-200 dark:border-zinc-700 font-mono text-[11px] shadow-2xs">
                                            {student.cohort || 'Unknown'}
                                        </span>
                                    </td>
                                    <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap text-xs font-semibold text-zinc-800 dark:text-zinc-200">
                                        {student.country ? `${student.country} ${student.county ? `(${student.county})` : ''}` : 'Unknown'}
                                    </td>
                                    <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full shadow-2xs ${
                                            student.status === 'Current' || student.status === 'Paid 1020' || student.status === 'Paid 1000' || student.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-300 dark:bg-emerald-950/90 dark:text-emerald-300 dark:border-emerald-500/80' : 
                                            student.status === 'Alumni' ? 'bg-rose-50 text-rose-700 border border-rose-300 dark:bg-rose-950/90 dark:text-rose-300 dark:border-rose-500/80' : 
                                            student.status === 'pending_verification' || student.status === 'Verifying' ? 'bg-amber-50 text-amber-700 border border-amber-300 dark:bg-amber-950/90 dark:text-amber-300 dark:border-amber-500/80' : 
                                            student.status === 'pending_payment' ? 'bg-orange-50 text-orange-700 border border-orange-300 dark:bg-orange-950/90 dark:text-orange-300 dark:border-orange-500/80' : 
                                            student.status === 'Interview Scheduled' || student.status === 'INTERVIEW_SCHEDULED' ? 'bg-sky-50 text-sky-700 border border-sky-300 dark:bg-sky-950/90 dark:text-sky-300 dark:border-sky-500/80' : 
                                            'bg-zinc-100 text-zinc-700 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700'}`}>
                                            {student.status === 'pending_payment' ? 'Pending Payment' : student.status === 'pending_verification' ? 'Pending Verification' : student.status}
                                        </span>
                                    </td>
                                    <td className="p-3 border-r border-zinc-200 dark:border-zinc-800 whitespace-nowrap">
                                        {student.sponsor ? (
                                            <span className="px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full bg-purple-50 text-purple-700 border border-purple-300 dark:bg-purple-950/90 dark:text-purple-300 dark:border-purple-500/80 inline-flex items-center gap-1 shadow-2xs" title={`Sponsor: ${student.sponsor}`}>
                                                🏢 {student.sponsor.length > 18 ? student.sponsor.substring(0, 16) + '...' : student.sponsor}
                                            </span>
                                        ) : (
                                            <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border shadow-2xs ${
                                                (student.sponsorship === 'self' || student.sponsorship === 'pay_full' || student.sponsorship === 'pay_slowly') ? 'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700' : 
                                                (student.sponsorship === 'nef') ? 'bg-amber-50 text-amber-700 border border-amber-300 dark:bg-amber-950/90 dark:text-amber-300 dark:border-amber-500/80' : 
                                                (student.sponsorship === 'corporate') ? 'bg-purple-50 text-purple-700 border border-purple-300 dark:bg-purple-950/90 dark:text-purple-300 dark:border-purple-500/80' : 
                                                'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800/80 dark:text-zinc-300 dark:border-zinc-700'}`}>
                                                {student.sponsorship === 'nef' ? 'Bursary' : student.sponsorship === 'corporate' ? 'Corporate' : (student.sponsorship === 'self' || student.sponsorship === 'pay_full' || student.sponsorship === 'pay_slowly') ? 'Self-Funded' : student.sponsorship === 'unsure' ? 'Needs Help' : 'N/A'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 text-center whitespace-nowrap">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDelete(student.id); }}
                                            className="text-zinc-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                            title="Delete record"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Google Sheets Status Bar Footer */}
                <div className="px-4 py-2.5 bg-zinc-50 dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-800 flex flex-wrap items-center justify-between text-[11px] font-mono text-zinc-600 dark:text-zinc-400">
                    <div className="flex items-center gap-4">
                        <span className="font-bold text-zinc-800 dark:text-zinc-200">📋 Sheet: students_registry</span>
                        <span>Showing {sortedStudents.length} of {students.length} rows</span>
                        {pinnedColumn !== 'none' && (
                            <span className="text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1">
                                <Pin size={10} className="fill-amber-500" /> Pane Frozen ({pinnedColumn})
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">● {paidCount} Paid (Gate 1)</span>
                        <span className="text-purple-600 dark:text-purple-400 font-bold">● {sponsoredCount} Sponsored</span>
                        <span className="text-zinc-400 dark:text-zinc-500">| Google Sheets Schema Ready</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentManager;
